import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Button, Paper, LinearProgress, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, Checkbox,
  TextField, InputAdornment, IconButton, useTheme,
} from '@mui/material';
import {
  HowToRegOutlined, AssignmentTurnedInOutlined, ErrorOutline, Search, Clear,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionCycleService } from '../../services/admissions/admissionCycleService';
import { HandoverPreviewRow, HandoverRowResult } from '../../services/admissions/handoverService';
import { AdmissionCycle } from '../../types/admissions';
import EmptyState from '../../components/common/EmptyState';
import TableRowSkeletons from '../../components/common/TableRowSkeletons';
import HandoverResultsDialog from '../../components/admissions/dialogs/HandoverResultsDialog';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import SearchableSelect from '../../components/common/SearchableSelect';
import { statusLabel, ToneChip } from '../../components/common/StatusChip';
import KpiTile from '../../components/reports/finance/KpiTile';
import {
  TabToolbar, TableCard, GroupLabel, tableHeadCellSx, clickableRowSx,
} from '../../components/admissions/tabLayout';
import { fmtClassName } from '../../utils/pdf/format';
import { useHandover, classNameOf } from '../../components/admissions/handover/useHandover';
import { HandoverSectionSelect, AssignSectionMenu } from '../../components/admissions/handover/HandoverSectionPicker';
import HandoverConfirmDialog from '../../components/admissions/handover/HandoverConfirmDialog';
import HandoverApplicantName from '../../components/admissions/handover/HandoverApplicantName';

/**
 * AdmissionHandoverPage — the batch handover runner. Lists Confirmed
 * applications that are ready to enroll (and the ones blocked by missing
 * preconditions), lets the operator check a subset, runs the batch, shows
 * per-row results.
 *
 * This is the one place the enrollment handover actually fires. Per
 * backend §6, each application runs in its own transaction so one bad row
 * never kills the batch.
 */

const AdmissionHandoverPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canWrite } = useModulePermissions();
  const [cycles, setCycles] = useState<AdmissionCycle[]>([]);
  const [cycleFilter, setCycleFilter] = useState<string>('');
  const [results, setResults] = useState<HandoverRowResult[] | null>(null);
  // Snapshot of the rows that were run — the preview reloads afterwards and
  // drops enrolled rows, but the summary still needs their names.
  const [ranRows, setRanRows] = useState<HandoverPreviewRow[]>([]);
  const {
    rows, summary, loading, load, searchInput, setSearchInput, search,
    visibleRows, readyRows, blockedRows, selected, allReadySelected, toggleAll, toggleOne,
    sectionOf, setSection, setSectionForSelected, sectionedClasses, missingSection, selectionSummary, running, run,
  } = useHandover({ cycleId: cycleFilter });
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Load cycles for the filter dropdown. We only show Open or Closed cycles
  // — Drafts have no applications, Archived cycles are read-only.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await admissionCycleService.list({ limit: 100 });
        if (!cancelled) {
          const filtered = res.items.filter(c => c.status === 'Open' || c.status === 'Closed');
          setCycles(filtered);
        }
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err?.message || 'Failed to load cycles', { variant: 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  const runHandover = async () => {
    const out = await run();
    setConfirmOpen(false);
    if (!out) return;
    setRanRows(out.ranRows);
    setResults(out.response.results);
  };

  const classLabel = (r: HandoverPreviewRow) => fmtClassName(classNameOf(r)) || '—';
  const showEmpty = !loading && rows.length === 0;

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Enrollment handover</Typography>
          <Typography variant="body2" color="text.secondary">
            Turn confirmed applicants into enrolled students. Each student is enrolled on their own, so one problem never stops the rest.
          </Typography>
        </Box>
        {canWrite('admissions') && (
          <Button
            variant="contained"
            size="small"
            disableElevation
            startIcon={running ? undefined : <HowToRegOutlined />}
            onClick={() => setConfirmOpen(true)}
            disabled={running || selected.size === 0 || missingSection > 0}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {running ? 'Enrolling…'
              : selected.size === 0 ? 'Enroll selected'
              : missingSection > 0 ? `Choose a section for ${missingSection}`
              : `Enroll ${selected.size} student${selected.size === 1 ? '' : 's'}`}
          </Button>
        )}
      </Box>

      {/* ── Snapshot ───────────────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Confirmed', value: summary?.total ?? 0, sub: 'Accepted their offer and paid', icon: <AssignmentTurnedInOutlined />, tone: 'primary' as const },
          { label: 'Ready to enroll', value: summary?.ready ?? 0, sub: 'Can be handed over now', icon: <HowToRegOutlined />, tone: 'success' as const },
          { label: 'Needs attention', value: summary?.blocked ?? 0, sub: 'Something to finish first', icon: <ErrorOutline />, tone: 'warning' as const },
        ].map(t => (
          <Grid key={t.label} size={{ xs: 12, sm: 4 }}>
            <KpiTile label={t.label} value={String(t.value)} sub={t.sub} icon={t.icon} tone={t.tone} loading={loading && !summary} />
          </Grid>
        ))}
      </Grid>

      {/* ── Board ──────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <TabToolbar
          onRefresh={load}
          refreshing={loading}
          summary={rows.length > 0 ? `${readyRows.length} ready · ${blockedRows.length} need attention` : undefined}
        >
          <SearchableSelect
            label="Cycle"
            value={cycleFilter}
            onChange={setCycleFilter}
            options={[
              { value: '', label: 'All' },
              ...cycles.map(c => ({
                value: c._id,
                label: c.name,
                secondary: statusLabel({ kind: 'admissionCycle', value: c.status }),
              })),
            ]}
            placeholder="All cycles"
            minWidth={220}
          />
          <TextField
            size="small"
            placeholder="Search by name, application no. or class…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearchInput('')}><Clear fontSize="small" /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ flex: 1, minWidth: 220, maxWidth: 380 }}
          />
        </TabToolbar>

        <Box sx={{ position: 'relative', px: 2.5, py: 2.5 }}>
          {loading && rows.length > 0 && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />
          )}

          {loading && rows.length === 0 ? (
            <TableCard>
              <Table size="small"><TableBody><TableRowSkeletons columns={5} rows={4} /></TableBody></Table>
            </TableCard>
          ) : showEmpty ? (
            <EmptyState
              icon={<HowToRegOutlined />}
              title="Nothing to hand over right now"
              description="Applicants appear here once they are Confirmed — offer accepted, admission fee paid and family details complete."
            />
          ) : visibleRows.length === 0 ? (
            <EmptyState
              icon={<Search />}
              title={`No applicant matches "${search}"`}
              action={{ label: 'Clear search', onClick: () => setSearchInput('') }}
            />
          ) : (
            <Stack spacing={3} sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms ease' }}>
              {readyRows.length > 0 && (
                <Box>
                  <GroupLabel
                    count={readyRows.length}
                    action={selected.size > 0 ? (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <AssignSectionMenu classes={sectionedClasses} onAssign={setSectionForSelected} disabled={running} />
                        <Typography variant="caption" color="text.secondary">{selected.size} selected</Typography>
                      </Stack>
                    ) : undefined}
                  >
                    Ready to enroll
                  </GroupLabel>
                  <TableCard>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': tableHeadCellSx }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              indeterminate={selected.size > 0 && !allReadySelected}
                              checked={allReadySelected}
                              onChange={toggleAll}
                              disabled={loading}
                              inputProps={{ 'aria-label': 'Select all ready applicants' }}
                            />
                          </TableCell>
                          <TableCell>Applicant</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell>Section</TableCell>
                          <TableCell align="right">Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {readyRows.map(r => {
                          const picked = selected.has(r.applicationId);
                          return (
                            <TableRow
                              key={r.applicationId}
                              hover
                              selected={picked}
                              onClick={() => toggleOne(r.applicationId)}
                              sx={clickableRowSx}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox size="small" checked={picked} tabIndex={-1} inputProps={{ 'aria-label': `Select ${r.name}` }} />
                              </TableCell>
                              <TableCell><HandoverApplicantName row={r} /></TableCell>
                              <TableCell><Typography variant="body2">{classLabel(r)}</Typography></TableCell>
                              <TableCell>
                                <HandoverSectionSelect
                                  row={r}
                                  value={sectionOf[r.applicationId]}
                                  onChange={(id) => setSection(r.applicationId, id)}
                                  disabled={running}
                                  required={picked}
                                />
                              </TableCell>
                              <TableCell align="right"><ToneChip label="Ready" tone="success" /></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableCard>
                </Box>
              )}

              {blockedRows.length > 0 && (
                <Box>
                  <GroupLabel count={blockedRows.length}>Needs attention</GroupLabel>
                  <TableCard footer="Finish these on the applicant's Family and Decision tabs, then refresh — they move to Ready to enroll.">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': tableHeadCellSx }}>
                          <TableCell>Applicant</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell>What's missing</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {blockedRows.map(r => (
                          <TableRow key={r.applicationId} sx={{ '&:last-of-type td': { borderBottom: 0 } }}>
                            <TableCell><HandoverApplicantName row={r} /></TableCell>
                            <TableCell><Typography variant="body2">{classLabel(r)}</Typography></TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {r.blockers.map(b => <ToneChip key={b} label={b} tone="warning" />)}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableCard>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Paper>

      <HandoverConfirmDialog
        open={confirmOpen}
        summary={selectionSummary}
        running={running}
        onClose={() => setConfirmOpen(false)}
        onConfirm={runHandover}
      />
      {results && (
        <HandoverResultsDialog
          open
          results={results}
          previewRows={ranRows}
          onClose={() => setResults(null)}
          cycleName={cycles.find(c => c._id === cycleFilter)?.name}
        />
      )}
    </Box>
  );
};

export default AdmissionHandoverPage;
